const template = document.createElement("template");
template.innerHTML = /* html */ `
	<style>
		:host {
			--_swipe-drawer-duration: 0.5s;
			--_swipe-drawer-ease: cubic-bezier(0.32, 0.72, 0, 1);
			--_drag-bar-height: 44px;
			--_swipe-drawer-background: canvas;
			display: block;
			inset: 0;
			margin: 0;
			width: unset;
			height: unset;
			border: 0;
			padding: 0;
			background: transparent;
			transition: inset var(--_swipe-drawer-duration) var(--_swipe-drawer-ease);
		}

		:host:not([data-snapped="true"]) {
			transition-property: display, overlay;
			transition-behavior: allow-discrete;
			transition-duration: var(--_swipe-drawer-duration);
		}

		.drawer__scroller {
			height: 100%;
			width: 100%;
			flex-direction: column;
			align-items: center;
			display: grid;
			grid-template-columns: minmax(0, 1fr);
			grid-template-rows: 100dvh auto;
			justify-items: center;
			overflow-y: auto;
			overscroll-behavior: none;
			scroll-snap-type: y mandatory;

			&::after {
				content: "";
				width: 100%;
				height: 100dvh;
				grid-column: 1 / -1;
				grid-row: 1 / span 1;
			}
		}

		:host([data-dragging="true"]) .drawer__scroller {
			scroll-snap-type: none;
		}

		.drawer__curtain {
			opacity: 0;
			height: 100%;
			width: 100%;
			border: 0;
			display: block;
			grid-column: 1 / -1;
			grid-row: 1 / span 2;
		}

		.drawer__slide {
			width: 600px;
			max-height: 95dvb;
			max-width: 100%;
			position: relative;
			grid-column: 1 / -1;
			grid-row: 2 / span 1;
			place-self: end center;

			@media (hover: none) and (pointer: coarse) {
				&::after {
					content: "";
					position: absolute;
					inset: 0;
					background: var(--_swipe-drawer-background);
					translate: 0 100%;
				}
			}
		}

		.drawer__anchors {
			pointer-events: none;
			position: absolute;
			inset: 0;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			z-index: 10;

			.drawer__anchor {
				height: 50px;
				width: 100%;
				scroll-snap-align: end;

				&:first-of-type {
					translate: 0 -100%;
				}
			}
		}

		.drawer__content {
			width: 100%;
			height: 100%;
			z-index: 2;

			display: flex;
			flex-direction: column;
			justify-content: space-between;
			padding-top: var(--_drag-bar-height);
			background: var(--_swipe-drawer-background);
			border: 1px solid oklch(from currentColor l c h / 0.2);
			border-bottom: 0;
			border-radius: 12px 12px 0 0;

			transition-property: translate;
			transition-duration: var(--_swipe-drawer-duration);
			transition-timing-function: var(--_swipe-drawer-ease);
			translate: 0 100%;
		}

		.drawer__content-container {
			padding: 0.5rlh;
		}

		.drawer__drag {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: space-evenly;
			height: var(--_drag-bar-height);
			background: #0000;
			border: 0;
			font-size: 0.875rem;
			outline-color: #0000;
			border-bottom: 1px solid oklch(from currentColor l c h / 0.2);
			position: absolute;
			top: 0;
			width: 100%;

			& span:first-of-type {
				width: 8ch;
				height: 6px;
				border-radius: 10px;
				background: oklch(from currentColor l c h / 0.2);
			}

			&:focus-visible {
				outline: none;

				& span:first-of-type {
					background: oklch(from currentColor l c h / 0.6);
				}
			}
		}

		:host(:popover-open) {
			.drawer__content {
				translate: 0 0;
				@starting-style {
					translate: 0 100%;
				}
			}
		}

		:host(::backdrop) {
			transition-property: opacity, overlay;
			transition-behavior: allow-discrete;
			transition-duration: var(--_swipe-drawer-duration);
			transition-timing-function: var(--_swipe-drawer-ease);
			opacity: 0;
			background: hsl(0 0% 0% / 0.5);
		}

		:host(:popover-open)::backdrop {
			opacity: 1;
			@starting-style {
				opacity: 0;
			}
		}
	</style>
	<div class="drawer__scroller">
		<button
			popovertargetaction="hide"
			popovertarget="{{ id }}"
			class="drawer__curtain"
			aria-label="{{ 'general.close' | t }}"
		></button>

		<div class="drawer__slide">
			<div class="drawer__anchors">
				<div class="drawer__anchor"></div>
				<div class="drawer__anchor"></div>
			</div>

			<div class="drawer__content">
				<button
					autofocus
					class="drawer__drag"
					popovertargetaction="hide"
					popovertarget="{{ id }}"
					aria-label="{{ 'general.close' | t }}"
				>
					<span></span>
				</button>

				<div class="drawer__content-container">
					<slot></slot>
				</div>
			</div>
		</div>
	</div>
`;

class SwipeDrawer extends HTMLElement {
	/** @type {HTMLElement} */
	anchor;
	/** @type {HTMLElement} */
	scroller;
	/** @type {HTMLElement} */
	slide;
	/** @type {HTMLElement} */
	content;
	/** @type {HTMLButtonElement} */
	drag;

	constructor() {
		super();

		this.handleIntersection = this.handleIntersection.bind(this);
		this.handleFocus = this.handleFocus.bind(this);

		this.attachShadow({ mode: "open", delegatesFocus: true });
		this.shadowRoot.append(template.content.cloneNode(true));
	}

	connectedCallback() {
		this.anchor = this.shadowRoot.querySelector(".drawer__anchor");
		this.scroller = this.shadowRoot.querySelector(".drawer__scroller");
		this.slide = this.shadowRoot.querySelector(".drawer__slide");
		this.content = this.shadowRoot.querySelector(".drawer__content");
		this.drag = this.shadowRoot.querySelector(".drawer__drag");
		this.observer = new IntersectionObserver(this.handleIntersection, {
			root: this,
			rootMargin: "0px 0px -1px 0px",
			threshold: 1,
		});
		this.addEventListener("toggle", this);
		this.attachDrag();
		this.autofocus = true;
		for (const button of this.shadowRoot.querySelectorAll("[popovertarget]")) {
			button.popoverTargetElement = this;
		}
	}

	disconnectedCallback() {
		this.observer?.disconnect();
		this.removeEventListener("toggle", this);
	}

	handleEvent(event) {
		this[`on${event.type}`](event);
	}

	async ontoggle(event) {
		this.content.style.willChange = "translate";

		if (event.newState === "open") {
			await Promise.allSettled(this.getAnimations({ subtree: true }).map((a) => a.finished));
			this.observer?.observe(this.anchor);
			window.addEventListener("focus", this.handleFocus, true);
		}
		if (event.newState === "closed") {
			this.dataset.snapped = false;
			this.observer?.unobserve(this.anchor);
			window.removeEventListener("focus", this.handleFocus, true);
		}
	}

	handleFocus(event) {
		if (!this.contains(event.target) || !event.target) {
			window.removeEventListener("focus", this.handleFocus, true);
			this.hidePopover();
		}
	}

	handleIntersection(entries) {
		const { isIntersecting, intersectionRatio } = entries[0];
		const isVisible = intersectionRatio === 1;

		if (
			!isVisible &&
			!isIntersecting &&
			this.scroller.scrollTop - window.visualViewport.offsetTop < this.slide.offsetHeight * 0.5
		) {
			this.dataset.snapped = true;
			this.hidePopover();
			this.observer?.disconnect();
		}
	}

	attachDrag() {
		let startY = 0;
		let drag = 0;
		let scrollStart;

		const reset = () => {
			startY = drag = 0;
			const top = this.scroller.scrollTop < scrollStart * 0.5 ? 0 : scrollStart;

			const handleScroll = () => {
				if (this.scroller.scrollTop === top) {
					this.dataset.dragging = false;
					this.scroller.removeEventListener("scroll", handleScroll);
				}
			};
			this.scroller.addEventListener("scroll", handleScroll);

			this.scroller.scrollTo({
				top,
				behavior: "smooth",
			});
			handleScroll();
		};

		const handle = ({ y }) => {
			drag += Math.abs(y - startY);
			this.scroller.scrollTo({
				top: scrollStart - (y - startY),
				behavior: "instant",
			});
		};
		const teardown = (event) => {
			if (event.target.tagName !== "BUTTON") {
				reset();
			}
			document.removeEventListener("mousemove", handle);
			document.removeEventListener("mouseup", teardown);
		};
		const activate = ({ y }) => {
			startY = y;
			scrollStart = this.scroller.scrollTop;
			this.dataset.dragging = true;
			document.addEventListener("mousemove", handle);
			document.addEventListener("mouseup", teardown);
		};
		this.addEventListener("click", (event) => {
			if (drag > 5) event.preventDefault();
			reset();
		});
		this.addEventListener("mousedown", activate);
	}
}

if (!customElements.get("swipe-drawer")) {
	customElements.define("swipe-drawer", SwipeDrawer);
}
